import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useAuth } from '../AuthContext';
import ProfileHeader from '../components/ProfileHeader';
import {
  acceptOrganizationInvite,
  declineOrganizationInvite,
  expireOrganizationInvite,
  getOrganizationInviteByCode,
  isOrganizationInviteExpired,
} from '../utils/organizationInvites';
import { canAcceptOrganizationInvite } from '../utils/platformAccess';
import { identityEmailsMatch } from '../utils/identityEmail';
import { getInvitePostAcceptPath } from '../utils/organizationExperience';
import { setPreferredOrganizationId } from '../utils/organizationSelection';
import { getOrganizationMemberships } from '../utils/organizationContext';

function AcceptOrganizationInvitePage() {
  const { inviteCode = '' } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData, refreshUserData } = useAuth() || {};
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [nextPath, setNextPath] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadInvite() {
      setLoading(true);
      try {
        const nextInvite = await getOrganizationInviteByCode(db, inviteCode);
        if (!nextInvite?.inviteId) {
          if (!cancelled) {
            setInvite(null);
            setError('This invite link is invalid or no longer exists.');
          }
          return;
        }

        if (isOrganizationInviteExpired(nextInvite) && nextInvite.status === 'pending') {
          await expireOrganizationInvite(db, { inviteId: nextInvite.inviteId });
          if (!cancelled) {
            setInvite({ ...nextInvite, status: 'expired' });
          }
          return;
        }

        if (!cancelled) {
          setInvite(nextInvite);
        }
      } catch (loadError) {
        console.error(loadError);
        if (!cancelled) {
          setError('We could not load this invite right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadInvite();

    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  const canAccept = useMemo(
    () => canAcceptOrganizationInvite(invite || {}, { currentUser, userData }),
    [currentUser, invite, userData]
  );

  const isAlreadyMember = useMemo(() => {
    if (!invite?.organizationId) return false;

    return getOrganizationMemberships(userData || {}).some(
      (membership) => membership.organizationId === invite.organizationId
    );
  }, [invite, userData]);

  const resolveNextPath = (inviteRecord = invite) =>
    getInvitePostAcceptPath(inviteRecord || {}, { userData });

  // Build a /login or /signup link that preserves BOTH the invite redirect and the invited email, so a
  // first-time invited user lands back on this invite after authenticating, under the correct identity.
  // `extra` adds flags such as reset=1 (deep-link the reset flow on the login screen).
  const inviteAuthPath = (base, extra = {}) => {
    const params = new URLSearchParams();
    params.set('redirect', `/invite/${inviteCode}`);
    const invitedEmail = invite?.email || invite?.emailNormalized || '';
    if (invitedEmail) params.set('email', invitedEmail);
    Object.entries(extra).forEach(([k, v]) => { if (v != null && v !== '') params.set(k, String(v)); });
    return `${base}?${params.toString()}`;
  };

  // Pass 88.4 — wrong-account recovery: sign the current (wrong) account out and continue to sign in as the
  // invited address, returning straight back to this invite. We NEVER silently deny; the user is given a path.
  const handleSignOutAndContinue = async () => {
    try { await signOut(auth); } catch (signOutError) { console.error(signOutError); }
    navigate(inviteAuthPath('/login'));
  };

  // Does this invite already correspond to a KNOWN existing account? An invite that explicitly targets a
  // canonical member (seeded staff, e.g. Sam) or has a resolved linkedUserId means an account very likely
  // already exists → Sign in is the primary action and we do NOT lead with "Create account".
  const inviteKnowsExistingAccount = Boolean(
    invite?.targetMemberUserId || invite?.targetMemberId || invite?.linkedUserId
  );

  const handleAccept = async () => {
    try {
      const acceptedInvite = await acceptOrganizationInvite(db, {
        inviteCode,
        currentUser,
        userData,
      });
      setInvite(acceptedInvite);
      const orgId = acceptedInvite.organizationId || invite?.organizationId || '';
      setPreferredOrganizationId(orgId);
      setMessage('Invite accepted. Setting up your workspace…');
      setError('');
      // The member link (member.authUserId → this uid) just completed. Refresh AuthContext and wait for the
      // canonical membership to become visible BEFORE navigating, so the destination workspace guard sees the
      // entitlement — no manual browser reload. refreshUserData returns the freshly resolved userData.
      let refreshed = userData;
      for (let attempt = 0; attempt < 6; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        const next = typeof refreshUserData === 'function' ? await refreshUserData() : null;
        if (next) refreshed = next;
        const visible = getOrganizationMemberships(refreshed || {}).some(
          (membership) => membership.organizationId === orgId
        );
        if (visible) break;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { window.setTimeout(resolve, 350); });
      }
      const redirectPath = getInvitePostAcceptPath(acceptedInvite || {}, { userData: refreshed || userData });
      setNextPath(redirectPath);
      navigate(redirectPath);
    } catch (acceptError) {
      console.error(acceptError);
      setError('We could not accept this invite right now.');
    }
  };

  const handleDecline = async () => {
    try {
      const declinedInvite = await declineOrganizationInvite(db, {
        inviteCode,
        currentUser,
        userData,
      });
      setInvite(declinedInvite);
      setMessage('Invite declined.');
      setError('');
    } catch (declineError) {
      console.error(declineError);
      setError('We could not decline this invite right now.');
    }
  };

  const currentEmail = String(currentUser?.email || userData?.email || '').trim().toLowerCase();
  const inviteEmail = String(invite?.emailNormalized || invite?.email || '').trim().toLowerCase();
  // A genuine WRONG-ACCOUNT state: a real signed-in email that is NOT the same login mailbox as the invite
  // (gmail.com ≡ googlemail.com is treated as the same mailbox, so it is NOT flagged wrong).
  const wrongAccount = Boolean(currentEmail && inviteEmail && !identityEmailsMatch(currentEmail, inviteEmail));
  const resolvedNextPath = nextPath || resolveNextPath(invite);

  return (
    <div className="mx-auto max-w-3xl p-4">
      <ProfileHeader />
      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Organization Invite</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-950">Join Organization</h1>

        {loading ? (
          <p className="mt-4 text-sm text-slate-600">Loading invite...</p>
        ) : (
          <>
            {message ? <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</p> : null}
            {error ? <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p> : null}

            {!invite?.inviteId ? null : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
                <div><strong>Organization:</strong> {invite.organizationName || invite.organizationId}</div>
                <div className="mt-1"><strong>Role:</strong> {invite.role}</div>
                <div className="mt-1"><strong>Status:</strong> {invite.status}</div>
                <div className="mt-1"><strong>Email:</strong> {invite.email}</div>
                {invite.note ? <div className="mt-1"><strong>Note:</strong> {invite.note}</div> : null}
              </div>
            )}

            {!currentUser ? (
              <div data-testid="invite-signed-out" className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                Use <strong>{invite?.email || 'the invited email address'}</strong> to accept this invitation.
                {inviteKnowsExistingAccount
                  ? ' You already have a Voletus account for this address — sign in to continue.'
                  : ' If you already have an account, sign in. First time here? Create your account — it takes a moment.'}
                {/* Every action carries the invite redirect AND the invited email so authentication happens under
                    the correct identity and returns straight back here to accept. Sign in is the PRIMARY action;
                    Reset password is always offered; Create account appears only when no existing account is known. */}
                <div className="mt-3 flex flex-wrap gap-3">
                  <Link
                    to={inviteAuthPath('/login')}
                    data-testid="invite-signin"
                    className="rounded-xl bg-slate-900 px-4 py-2 font-semibold text-white"
                  >
                    Sign In To Continue
                  </Link>
                  <Link
                    to={inviteAuthPath('/login', { reset: 1 })}
                    data-testid="invite-reset"
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-800"
                  >
                    Reset Password
                  </Link>
                  {!inviteKnowsExistingAccount ? (
                    <Link
                      to={inviteAuthPath('/signup')}
                      data-testid="invite-create-account"
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-800"
                    >
                      Create Account
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : invite?.status === 'accepted' ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
                This invite has already been accepted.
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setPreferredOrganizationId(invite.organizationId || '');
                      navigate(resolvedNextPath);
                    }}
                    className="rounded-xl bg-emerald-700 px-4 py-2 font-semibold text-white"
                  >
                    Continue
                  </button>
                </div>
              </div>
            ) : invite?.status === 'declined' ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
                This invite has already been declined.
              </div>
            ) : invite?.status === 'expired' ? (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                This invite has expired. Ask the organization admin to send a new one.
              </div>
            ) : invite?.status === 'canceled' ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-700">
                This invite has been canceled.
              </div>
            ) : !canAccept ? (
              <div data-testid="invite-wrong-account" className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
                {wrongAccount ? (
                  <>
                    You're signed in as <strong>{currentEmail}</strong>, but this invitation was sent to{' '}
                    <strong>{inviteEmail}</strong>.
                    <div className="mt-3 flex flex-wrap gap-3">
                      <button
                        type="button"
                        data-testid="signout-continue"
                        onClick={handleSignOutAndContinue}
                        className="rounded-xl bg-slate-900 px-4 py-2 font-semibold text-white"
                      >
                        Sign out and continue with {inviteEmail}
                      </button>
                      <Link
                        to="/dashboard"
                        data-testid="wrong-account-back"
                        className="rounded-xl border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-800"
                      >
                        Back
                      </Link>
                    </div>
                  </>
                ) : (
                  // Not a wrong-account case (e.g. the account/email is still resolving) — explain plainly and
                  // offer a path rather than a bare denial.
                  <>
                    We couldn't confirm this invitation for your account yet.
                    <div className="mt-3 flex flex-wrap gap-3">
                      <button
                        type="button"
                        data-testid="signout-continue"
                        onClick={handleSignOutAndContinue}
                        className="rounded-xl bg-slate-900 px-4 py-2 font-semibold text-white"
                      >
                        Sign in as {inviteEmail || 'the invited email'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {isAlreadyMember ? (
                  <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-5 text-sm text-sky-800">
                    You already appear to be a member of this organization. You can continue directly or accept the invite again to refresh the link state.
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => {
                          setPreferredOrganizationId(invite.organizationId || '');
                          navigate(resolvedNextPath);
                        }}
                        className="rounded-xl bg-sky-700 px-4 py-2 font-semibold text-white"
                      >
                        Continue To Workspace
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleAccept}
                    className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                  >
                    Accept Invite
                  </button>
                  <button
                    type="button"
                    onClick={handleDecline}
                    className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                  >
                    Decline Invite
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}

export default AcceptOrganizationInvitePage;
